import React from "react";
import { Users, AlertCircle, ArrowRight, Replace, SkipForward, Combine } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { translateFr as tfr } from "@/i18n";

const ACTIONS = [
  { v: "skip", label: "Ignorer", Icon: SkipForward, helper: tfr("Garder la fiche existante") },
  { v: "replace", label: "Remplacer", Icon: Replace, helper: tfr("Écraser avec les nouvelles données") },
  { v: "merge", label: "Fusionner", Icon: Combine, helper: tfr("Compléter les champs vides") },
];

/**
 * ImportDuplicates — Step 3.
 * Displays each detected duplicate (existing player vs imported row) with
 * 3 action choices: skip / replace / merge.
 * decisions: { [rowIndex]: "skip" | "replace" | "merge" }
 */
export default function ImportDuplicates({ duplicates = [], decisions = {}, onChange, mapping = {}, rows = [] }) {
  const total = duplicates.length;
  const decided = Object.keys(decisions).length;

  const setAll = (v) => {
    const next = {};
    duplicates.forEach((d) => { next[d.index] = v; });
    onChange?.(next);
  };

  const setOne = (idx, v) => {
    onChange?.({ ...decisions, [idx]: v });
  };

  if (total === 0) {
    return (
      <div className="rounded-2xl border border-success/20 bg-success/5 p-6 text-center" data-testid="dup-empty">
        <div className="h-12 w-12 mx-auto rounded-2xl bg-success/15 border border-success/30 text-success flex items-center justify-center mb-3">
          <Users className="h-5 w-5" />
        </div>
        <div className="text-base font-semibold">{tfr("Aucun doublon détecté")}</div>
        <div className="text-xs text-muted-foreground mt-1">
          {tfr("Tous vos joueurs sont uniques. Vous pouvez continuer en toute sécurité.")}
        </div>
      </div>
    );
  }

  // Helper to extract a column value from a row using mapping
  const valFor = (row, target) => {
    const col = Object.keys(mapping).find((k) => mapping[k] === target);
    return col ? (row[col] || "") : "";
  };

  return (
    <div className="space-y-4" data-testid="import-step-duplicates">
      {/* Summary header */}
      <div className="flex items-center justify-between flex-wrap gap-3 rounded-2xl border border-warning/20 bg-warning/[0.04] p-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-warning/15 border border-warning/30 text-warning flex items-center justify-center shrink-0">
            <AlertCircle className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold">
              {total} {total > 1 ? tfr("doublons détectés") : tfr("doublon détecté")}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">
              {tfr("Choisissez une action pour chaque joueur déjà présent dans votre base.")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[11px] text-muted-foreground dark:text-muted-foreground/70 mr-1">Tout :</span>
          {ACTIONS.map((a) => (
            <button
              key={a.v}
              onClick={() => setAll(a.v)}
              className="text-[11px] rounded-md px-2.5 py-1 border border-border dark:border-border hover:bg-accent dark:hover:bg-secondary/60 hover:border-slate-400 dark:hover:border-foreground/25 transition-colors"
              data-testid={`dup-bulk-${a.v}`}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>

      {/* Per-duplicate rows */}
      <div className="space-y-3">
        {duplicates.map((d) => {
          const row = rows[d.index] || {};
          const importedName = d.row_name || valFor(row, "name");
          const importedClub = d.row_club || valFor(row, "club");
          const importedPos = valFor(row, "position");
          const decision = decisions[d.index];
          return (
            <div
              key={d.index}
              className="rounded-2xl border border-border dark:border-border bg-card dark:bg-surface/50 overflow-hidden"
              data-testid={`dup-item-${d.index}`}
            >
              {/* Comparison row */}
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-3 border-b border-border/70 dark:border-border">
                {/* Existing */}
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground dark:text-muted-foreground/70 mb-1">{tfr("Déjà dans Athlynx")}</div>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold truncate">{d.existing?.name || "—"}</span>
                    {d.existing?.status && (
                      <Badge variant="outline" className="text-[9px] border-border dark:border-border text-muted-foreground">
                        {d.existing.status}
                      </Badge>
                    )}
                  </div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {d.existing?.club || "—"}
                    {d.existing?.position ? ` · ${d.existing.position}` : ""}
                  </div>
                </div>
                {/* Arrow */}
                <ArrowRight className="hidden md:block h-4 w-4 text-muted-foreground/40 mx-auto" />
                {/* Imported */}
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground dark:text-muted-foreground/70 mb-1">{tfr("Dans votre fichier")}</div>
                  <div className="text-sm font-semibold truncate">{importedName || "—"}</div>
                  <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                    {importedClub || "—"}
                    {importedPos ? ` · ${importedPos}` : ""}
                  </div>
                </div>
              </div>

              {/* Action selector */}
              <div className="grid grid-cols-3 divide-x divide-border/70 dark:divide-border/60">
                {ACTIONS.map((a) => {
                  const Icon = a.Icon;
                  const active = decision === a.v;
                  return (
                    <button
                      key={a.v}
                      onClick={() => setOne(d.index, a.v)}
                      className={cn(
                        "flex flex-col md:flex-row md:items-center md:justify-center gap-1.5 md:gap-2 py-3 px-2 text-[12px] font-medium transition-colors",
                        active
                          ? a.v === "replace"
                            ? "bg-primary/10 text-primary"
                            : a.v === "merge"
                              ? "bg-info/10 text-info"
                              : "bg-secondary dark:bg-secondary/50 text-foreground"
                          : "text-muted-foreground hover:bg-accent/50 dark:hover:bg-secondary/60 dark:bg-secondary/30 hover:text-foreground"
                      )}
                      data-testid={`dup-action-${d.index}-${a.v}`}
                      title={a.helper}
                    >
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span>{a.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer summary */}
      <div className="text-[11px] text-muted-foreground text-center">
        {decided} {tfr("sur")} {total} {total > 1 ? tfr("décisions prises") : tfr("décision prise")}.
        {decided < total && ` ${tfr("Une action sera demandée par défaut (Ignorer).")}`}
      </div>
    </div>
  );
}
